"use client";

import React, { useEffect, useState } from "react";

import ApplicationAbstract from "components/application/ApplicationAbstract";
import NoApplicationList from "components/application/NoApplication";
import FullSizeLoading from "components/FullSizeLoading";
import { SortTableField } from "components/ParkingTable";
import { ENUM_CUSTOMER_TABS } from "constants/customer";
import { SORT_DESTINATION, SortDestination } from "constants/sort";
import { ApplicationSortKey, CustomerApplication } from "graphql/type.ts";

const { ASC, DESC } = SORT_DESTINATION;

type ApplicationTableType = {
  applications?: CustomerApplication[];
  onSetSort: (field: keyof ApplicationSortKey, direction: SortDestination) => void;
  isValidating: boolean;
  tab: ENUM_CUSTOMER_TABS;
  updateMemo: (id: string, memo: string) => Promise<void>;
};

const ApplicationTable = ({ applications, onSetSort, isValidating, tab, updateMemo }: ApplicationTableType) => {
  const [sortField, setSortField] = useState<keyof ApplicationSortKey | null>(null); // 並び替えのフィールド
  const [sortDirection, setSortDirection] = useState<SortDestination | "">(""); // 並び替えの方向

  // Reset local sort state whenever tab changes
  useEffect(() => {
    setSortField(null);
    setSortDirection("");
  }, [tab]);

  const onSort = (field: keyof ApplicationSortKey) => {
    const newSortDirection = sortField !== field ? DESC : sortDirection === ASC ? DESC : ASC;
    setSortField(field);
    setSortDirection(newSortDirection);
    onSetSort(field, newSortDirection);
  };

  const headers: Record<ENUM_CUSTOMER_TABS, { name: string; field?: keyof ApplicationSortKey }[]> = {
    [ENUM_CUSTOMER_TABS.APPLICATION_CONTRACT]: [
      { name: "ステータス", field: "status" },
      { name: "契約者", field: "applicationContractor.name" as keyof ApplicationSortKey },
      { name: "駐車場", field: "applicationProperty.propertyName" as keyof ApplicationSortKey },
      { name: "利用予定車種", field: "applicationUsageInfo.sizeOfCar" as keyof ApplicationSortKey },
      { name: "申込日", field: "appliedAt" },
      { name: "利用開始日", field: "useStartDate" },
      { name: "申込経路", field: "type" as keyof ApplicationSortKey },
    ],
    [ENUM_CUSTOMER_TABS.UNDER_CONTRACT]: [
      { name: "ステータス", field: "status" },
      { name: "契約者", field: "applicationContractor.name" as keyof ApplicationSortKey },
      { name: "駐車場", field: "applicationProperty.propertyName" as keyof ApplicationSortKey },
      { name: "申込日", field: "appliedAt" },
      { name: "利用開始日", field: "useStartDate" },
      { name: "利用終了日", field: "cancellationDate" },
      { name: "申込経路", field: "type" as keyof ApplicationSortKey },
    ],
    [ENUM_CUSTOMER_TABS.CONTRACT_CANCELLED]: [
      { name: "ステータス", field: "status" },
      { name: "契約者", field: "applicationContractor.name" as keyof ApplicationSortKey },
      { name: "駐車場", field: "applicationProperty.propertyName" as keyof ApplicationSortKey },
      { name: "申込日", field: "appliedAt" },
      { name: "利用開始日", field: "useStartDate" },
      { name: "利用終了日", field: "cancellationDate" },
      { name: "申込経路", field: "type" as keyof ApplicationSortKey },
    ],
    [ENUM_CUSTOMER_TABS.WAITING_CONTRACT]: [
      { name: "ステータス", field: "status" },
      { name: "契約者", field: "applicationContractor.name" as keyof ApplicationSortKey },
      { name: "駐車場", field: "applicationProperty.propertyName" as keyof ApplicationSortKey },
      { name: "車種" },
      { name: "空き待ち予約日" },
      { name: "空き待ち期間" },
      { name: "申込経路", field: "type" as keyof ApplicationSortKey },
    ],
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="sticky top-0 z-10 mb-2 grid h-8 grid-cols-application rounded bg-[#E5E5E5] px-4">
        {headers[tab].map(({ name, field }, index) => (
          <SortTableField<ApplicationSortKey>
            key={name}
            field={field}
            onSort={onSort}
            name={name}
            sortDirection={sortDirection}
            sortField={sortField}
            index={index}
          />
        ))}
      </div>
      {isValidating ? (
        <FullSizeLoading />
      ) : applications && applications.length === 0 ? (
        <NoApplicationList tab={tab} />
      ) : (
        applications?.map((row, rowIndex) => (
          <ApplicationAbstract key={rowIndex} application={row} tab={tab} updateMemo={updateMemo} />
        ))
      )}
    </div>
  );
};

export default ApplicationTable;
